import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { ArrowLeft, ArrowRight, Loader2, MapPin, Send, Clock, ChevronRight, Mountain, Ship, Building2, Waves, Grape, Globe, Plane, Map, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { renderMarkdownSafe, sanitizeHtml } from "../lib/safeMarkdown";
import { useJsonLd, TAXINEARME_SITE_URL } from "../lib/seo";
import { veniceDisplayLabel } from "../components/VeniceSubPicker";
import SiteNav from "../components/SiteNav";
import CategoryButtonModal from "../components/CategoryButtonModal";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&q=80&w=2000";

const CATEGORY_ICONS = {
  "Dolomites": Mountain,
  "Cruise Terminals": Ship,
  "Cities": Building2,
  "Lakes": Waves,
  "Tuscany": Grape,
  "International": Globe,
  "Airports": Plane,
  "Tours": Map,
  "Local": MapPin,
  "Riviera": Waves,
};

const CATEGORY_COLORS = {
  "Dolomites": "from-blue-500/20 to-cyan-500/20",
  "Cruise Terminals": "from-purple-500/20 to-pink-500/20",
  "Cities": "from-amber-500/20 to-orange-500/20",
  "Lakes": "from-teal-500/20 to-emerald-500/20",
  "Tuscany": "from-rose-500/20 to-red-500/20",
  "International": "from-indigo-500/20 to-violet-500/20",
  "Airports": "from-slate-500/20 to-gray-500/20",
  "Tours": "from-lime-500/20 to-green-500/20",
  "Local": "from-sky-500/20 to-blue-500/20",
  "Riviera": "from-cyan-500/20 to-teal-500/20",
};

export default function RouteCategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openButton, setOpenButton] = useState(null);   // 1 | 2 | null

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/route-categories/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load category");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    if (data.meta_title) document.title = data.meta_title;
    return () => {
      document.title = "TaxiNearMe";
    };
  }, [data]);

  useJsonLd(
    data
      ? [
          {
            id: `tnm-cat-${slug}-breadcrumb`,
            data: {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${TAXINEARME_SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "All Routes", item: `${TAXINEARME_SITE_URL}/routes` },
                { "@type": "ListItem", position: 3, name: data.name, item: `${TAXINEARME_SITE_URL}/routes/category/${data.slug}` },
              ],
            },
          },
          data.routes?.length > 0
            ? {
                id: `tnm-cat-${slug}-itemlist`,
                data: {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: `${data.name} — TaxiNearMe`,
                  numberOfItems: data.routes.length,
                  itemListElement: data.routes.map((r, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: `${r.origin} → ${r.destination}`,
                    url: `${TAXINEARME_SITE_URL}/route/${r.slug}`,
                  })),
                },
              }
            : null,
        ].filter(Boolean)
      : []
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center gap-2">
        <SiteNav />
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <SiteNav />
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <h1 className="text-3xl font-serif mb-3">Category not available</h1>
          <Link to="/routes" className="text-[#C5A059] hover:underline" data-testid="category-back-link">
            ← Back to all routes
          </Link>
        </div>
      </div>
    );
  }

  const heroImage = data.hero_image_url || HERO_FALLBACK;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`category-page-${slug}`}>
      <SiteNav />

      {/* Hero */}
      <section
        className="relative h-[65vh] min-h-[420px] flex flex-col justify-end px-4 sm:px-8 lg:px-16 pt-24 pb-10"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 55%, rgba(10,10,10,1) 100%), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-[#C5A059] text-xs uppercase tracking-widest mb-3">
            Private transfers · {data.name}
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
            data-testid="category-h1"
          >
            {data.h1 || `${data.name} Transfers`}
          </h1>

          {/* Custom category buttons (up to 2) — sit directly under the H1,
              before the intro copy. Each opens a modal with admin-provided HTML. */}
          {(data.button1_label || data.button2_label) && (
            <div
              className="mt-10 flex flex-wrap justify-center gap-3"
              data-testid="category-custom-buttons"
            >
              {data.button1_label && (
                <button
                  type="button"
                  onClick={() => setOpenButton(1)}
                  className="inline-flex items-center gap-2 border border-[#C5A059]/50 bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[#C5A059] px-5 py-2.5 rounded-sm uppercase tracking-widest text-xs transition-colors"
                  data-testid="category-button-1"
                >
                  <Star className="w-4 h-4" /> {data.button1_label}
                </button>
              )}
              {data.button2_label && (
                <button
                  type="button"
                  onClick={() => setOpenButton(2)}
                  className="inline-flex items-center gap-2 border border-[#C5A059]/50 bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[#C5A059] px-5 py-2.5 rounded-sm uppercase tracking-widest text-xs transition-colors"
                  data-testid="category-button-2"
                >
                  <Star className="w-4 h-4" /> {data.button2_label}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro copy — accepts either raw HTML or the light markdown subset */}
      {data.intro_html && (
        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
          <div
            className="prose-custom text-white/80 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: /<[a-z][\s\S]*>/i.test(data.intro_html)
                ? sanitizeHtml(data.intro_html)
                : renderMarkdownSafe(data.intro_html),
            }}
            data-testid="category-intro"
          />
        </section>
      )}

      {openButton === 1 && data.button1_label && (
        <CategoryButtonModal
          title={data.button1_label}
          content={data.button1_content}
          onClose={() => setOpenButton(null)}
        />
      )}
      {openButton === 2 && data.button2_label && (
        <CategoryButtonModal
          title={data.button2_label}
          content={data.button2_content}
          onClose={() => setOpenButton(null)}
        />
      )}

      {/* Tailor-made CTA — sits between the intro copy and the route grid.
          Scrolls to the form section below when the form is enabled, otherwise
          opens a mailto to the admin as a fallback. */}
      {data.tailor_made_enabled && (
        <section className="max-w-4xl mx-auto px-4 sm:px-8 -mt-2 mb-8 flex justify-center">
          <a
            href="#tailor-made"
            className="group inline-flex items-center gap-3 border border-[#C5A059]/50 bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[#C5A059] px-6 py-3 rounded-sm uppercase tracking-widest text-sm transition-colors"
            data-testid="category-tailormade-cta"
          >
            <Send className="w-4 h-4" />
            Request a Tailor-Made Transfer
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </section>
      )}

      {/* Routes grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-serif text-white">
            {data.name} routes <span className="text-white/40 text-lg">({data.routes?.length || 0})</span>
          </h2>
          <Link to="/routes" className="text-[#C5A059] text-xs uppercase tracking-widest hover:underline" data-testid="category-all-routes-link">
            ← All routes
          </Link>
        </div>

        {(!data.routes || data.routes.length === 0) ? (
          <p className="text-white/50 text-center py-16" data-testid="category-empty">
            No published routes yet in this category. Request a tailor-made itinerary below.
          </p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-routes-grid">
            {data.routes.map((route) => {
              const Icon = CATEGORY_ICONS[route.category] || MapPin;
              const colorClass = CATEGORY_COLORS[route.category] || "from-gray-500/20 to-slate-500/20";
              return (
                <Link
                  key={route.route_id}
                  to={`/route/${route.slug}`}
                  className="group bg-[#121212] border border-white/5 rounded-sm overflow-hidden hover:border-[#C5A059]/30 transition-all duration-300"
                  data-testid={`category-route-${route.slug}`}
                >
                  {/* Category Header */}
                  <div className={`bg-gradient-to-r ${colorClass} px-4 py-2 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-white/70" />
                      <span className="text-xs text-white/70 uppercase tracking-wide">{route.category}</span>
                    </div>
                    {route.seo_priority === "Top" && (
                      <span className="text-[10px] bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5 rounded uppercase">Popular</span>
                    )}
                  </div>

                  {/* Route Info */}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white">{veniceDisplayLabel(route.origin)}</span>
                        <ArrowRight className="w-4 h-4 text-white/30" />
                        <span className="text-white font-semibold">{veniceDisplayLabel(route.destination)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-white/40">
                      {route.duration_hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          ~{route.duration_hours}h
                        </span>
                      )}
                      {route.distance_km && (
                        <span>{route.distance_km} km</span>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      <span className="text-sm text-white/50">View details &amp; book</span>
                      <ChevronRight className="w-5 h-5 text-[#C5A059] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Tailor-made request form */}
      {data.tailor_made_enabled && (
        <TailorMadeForm category={data.name} categorySlug={data.slug} headline={data.tailor_made_headline} />
      )}

      {/* Bottom back-nav */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-16">
        <Link to="/routes" className="inline-flex items-center gap-2 text-[#C5A059] hover:underline" data-testid="category-bottom-back">
          <ArrowLeft className="w-4 h-4" /> Back to all routes
        </Link>
      </section>
    </div>
  );
}

function TailorMadeForm({ category, categorySlug, headline }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    origin: "",
    destination: "",
    pickup_date: "",
    passengers: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error("Please fill in name, email and a short description");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tailormade-request`, {
        ...form,
        category,
        category_slug: categorySlug,
        passengers: form.passengers ? parseInt(form.passengers, 10) || null : null,
      });
      setSubmitted(true);
      toast.success("Request sent — we'll reply within a few hours");
    } catch {
      toast.error("Could not send your request. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12" data-testid="tailormade-thankyou">
        <div className="border border-[#C5A059]/40 rounded-sm p-8 sm:p-10 bg-gradient-to-b from-[#111] to-[#0A0A0A] text-center">
          <h3 className="text-2xl font-serif text-white mb-2">Request received — grazie!</h3>
          <p className="text-white/60">
            We&apos;ll reply within a few hours with a custom quote for your {category} transfer.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="tailor-made"
      className="max-w-3xl mx-auto px-4 sm:px-8 py-12 scroll-mt-24"
      data-testid="tailormade-form-section"
    >
      <div className="border border-[#C5A059]/40 rounded-sm p-6 sm:p-10 bg-gradient-to-b from-[#111] to-[#0A0A0A]">
        <div className="text-[#C5A059] text-xs uppercase tracking-widest mb-1">Tailor-made</div>
        <h3 className="text-2xl sm:text-3xl font-serif text-white mb-2">
          {headline || `Need a custom ${category} transfer?`}
        </h3>
        <p className="text-white/60 mb-6 text-sm">
          Tell us where you&apos;re going and when. We&apos;ll craft a private chauffeur itinerary and reply with a fixed all-in price.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="tailormade-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Full name *"
              className="input-dark"
              data-testid="tailormade-name"
              maxLength={120}
            />
            <Input
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="Email *"
              type="email"
              className="input-dark"
              data-testid="tailormade-email"
              maxLength={200}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="Phone (e.g. +39 393 …)"
              className="input-dark"
              data-testid="tailormade-phone"
              maxLength={40}
            />
            <Input
              value={form.pickup_date}
              onChange={(e) => update("pickup_date", e.target.value)}
              placeholder="Pickup date (yyyy-mm-dd or free text)"
              className="input-dark"
              data-testid="tailormade-date"
              maxLength={60}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={form.origin}
              onChange={(e) => update("origin", e.target.value)}
              placeholder={`Pickup location (e.g. Venice)`}
              className="input-dark"
              data-testid="tailormade-origin"
              maxLength={120}
            />
            <Input
              value={form.destination}
              onChange={(e) => update("destination", e.target.value)}
              placeholder={`Drop-off in ${category} or elsewhere`}
              className="input-dark"
              data-testid="tailormade-destination"
              maxLength={120}
            />
          </div>
          <Input
            value={form.passengers}
            onChange={(e) => update("passengers", e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="Passengers"
            className="input-dark"
            data-testid="tailormade-passengers"
            maxLength={3}
          />
          <textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Tell us your itinerary, timings, hotels or stops *"
            className="input-dark w-full min-h-[120px] p-3 rounded-sm"
            data-testid="tailormade-description"
            maxLength={2000}
          />
          <Button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full sm:w-auto uppercase tracking-widest"
            data-testid="tailormade-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send request <Send className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          <p className="text-[10px] text-white/40 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> No obligation — we&apos;ll reply with a fixed all-in price.
          </p>
        </form>
      </div>
    </section>
  );
}
